import React from "react";
import { Row, Col, Button, Table, FormGroup, Label } from "reactstrap";
import ReactDatetime from "components/CustomDatePicker";
import { formatDateForPicker, formatDateForAPI } from "utils/dateUtils";
import { useTranslation } from "react-i18next";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

const CONTRACT_OPTIONS = [
    "Retail Install Cont_WY_LAW_9-24_IDMS",
    "Retail Install Cont_CA_LAW_9-24_IDMS",
    "Retail Install Cont_TX_LAW_9-24_IDMS",
    "Retail Install Cont_NY_LAW_9-24_IDMS",
    "Retail Install Cont_FL_LAW_9-24_IDMS"
];

const COMPOUNDING_METHOD_OPTIONS = [
    "Exact Days",
    "Simple Interest",
    "Compound Interest",
    "Daily Compounding"
];

const ACCRUAL_METHOD_OPTIONS = [
    "Simple Interest",
    "Compound Interest",
    "Daily Accrual",
    "Monthly Accrual"
];

const SALES_TAX_DEFERRED_OPTIONS = [
    "No",
    "Yes"
];

const DAYS_IN_YEAR_OPTIONS = [
    "365 Days",
    "360 Days"
];

const AMORTIZATION_OPTIONS = [
    "US Rule",
    "Canadian Rule",
    "European Rule"
];

const BALANCE_SETUP_METHOD_OPTIONS = [
    "Book A New Loan Account",
    "Refinance Existing Loan",
    "Modify Existing Loan"
];

const DISCOUNT_METHOD_OPTIONS = [
    "(Straight-Line) Recognize Discount As Percentage Of Payment",
    "(Rule of 78) Recognize Discount As Percentage Of Payment",
    "No Discount Recognition"
];

const FREQUENCY_OPTIONS = [
    "Weekly",
    "Bi-Weekly",
    "Monthly",
    "Quarterly",
    "Semi-Annually",
    "Annually"
];

const LoanBalance = ({ onFormChange, initialValues, prefillKey, isTheme2 = false }) => {
    const { t } = useTranslation();
    const inputStyle14 = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;
    const dateInputStyle = isTheme2
      ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
      : { borderRadius: 0 };
    // Contract Settings State
    const [contract, setContract] = React.useState("");
    const [compoundingMethod, setCompoundingMethod] = React.useState("");
    const [accrualMethod, setAccrualMethod] = React.useState("");
    const [salesTaxDeferred, setSalesTaxDeferred] = React.useState("");
    const [daysInYear, setDaysInYear] = React.useState("");
    const [amortization, setAmortization] = React.useState("");

    // Disclosure Results State (calculated/read-only)
    const [loanRate, setLoanRate] = React.useState("0.0000%");
    const [apr, setApr] = React.useState("0.0000%");
    const [financeCharge, setFinanceCharge] = React.useState("$0.00");
    const [amountFinanced, setAmountFinanced] = React.useState("$0.00");
    const [totalOfPayments, setTotalOfPayments] = React.useState("$0.00");
    const [totalDownPayment, setTotalDownPayment] = React.useState("$12.00");

    // Balance Setup Method State
    const [balanceSetupMethod, setBalanceSetupMethod] = React.useState("Book A New Loan Account");
    const [contractDate, setContractDate] = React.useState("9/19/2025");
    const [originalFinanceCharge, setOriginalFinanceCharge] = React.useState("");
    const [currentDiscountAmount, setCurrentDiscountAmount] = React.useState("$0.00");
    const [originalAmtFinanced, setOriginalAmtFinanced] = React.useState("");
    const [originalSalesTax, setOriginalSalesTax] = React.useState("$0.00");
    const [discountMethod, setDiscountMethod] = React.useState("(Straight-Line) Recognize Discount As Percentage Of Payment");

    // Down Payment Schedule State
    const [downPaymentSchedules, setDownPaymentSchedules] = React.useState([
        { id: 1, type: "Cash Down", origAmount: "$0.00", dateDue: "" },
    ]);

    // Payment Schedule State
    const [paymentSchedules, setPaymentSchedules] = React.useState([
        { id: 1, schedule: 1, numPayments: "1", paymentAmount: "$0.00", frequency: "Weekly", firstPaymentDate: "", finalPayment: "$0.00" },
    ]);

    // Error state
    const [hasError, setHasError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");

    // Prefill from parent when provided
    React.useEffect(() => {
        const iv = initialValues || {};
        setContract(iv.contract ?? "");
        setCompoundingMethod(iv.compoundingMethod ?? "");
        setAccrualMethod(iv.accrualMethod ?? "");
        setSalesTaxDeferred(iv.salesTaxDeferred ?? "");
        setDaysInYear(iv.daysInYear ?? "");
        setAmortization(iv.amortization ?? "");
        setBalanceSetupMethod(iv.balanceSetupMethod ?? "Book A New Loan Account");
        setContractDate(iv.contractDate ?? "9/19/2025");
        setOriginalFinanceCharge(iv.originalFinanceCharge ?? "");
        setCurrentDiscountAmount(iv.currentDiscountAmount ?? "$0.00");
        setOriginalAmtFinanced(iv.originalAmtFinanced ?? "");
        setOriginalSalesTax(iv.originalSalesTax ?? "$0.00");
        setDiscountMethod(iv.discountMethod ?? "(Straight-Line) Recognize Discount As Percentage Of Payment");
    }, [prefillKey]);

    // Notify parent component of form changes
    React.useEffect(() => {
        if (onFormChange) {
            onFormChange({
                contract,
                compoundingMethod,
                accrualMethod,
                salesTaxDeferred,
                daysInYear,
                amortization,
                loanRate,
                apr,
                financeCharge,
                amountFinanced,
                totalOfPayments,
                totalDownPayment,
                balanceSetupMethod,
                contractDate,
                originalFinanceCharge,
                currentDiscountAmount,
                originalAmtFinanced,
                originalSalesTax,
                discountMethod,
                downPaymentSchedules,
                paymentSchedules,
            });
        }
    }, [contract, compoundingMethod, accrualMethod, salesTaxDeferred, daysInYear, amortization, loanRate, apr, financeCharge, amountFinanced, totalOfPayments, totalDownPayment, balanceSetupMethod, contractDate, originalFinanceCharge, currentDiscountAmount, originalAmtFinanced, originalSalesTax, discountMethod, downPaymentSchedules, paymentSchedules, onFormChange]);

    // Add new down payment schedule row
    const addDownPaymentSchedule = () => {
        const newId = Math.max(...downPaymentSchedules.map(item => item.id)) + 1;
        const newType = `Def Down ${downPaymentSchedules.filter(item => item.type.startsWith('Def Down')).length + 1}`;
        const newSchedule = {
            id: newId,
            type: newType,
            origAmount: "$0.00",
            dateDue: ""
        };
        setDownPaymentSchedules([...downPaymentSchedules, newSchedule]);
    };

    const removeDownPaymentSchedule = (id) => {
        setDownPaymentSchedules(prev => prev.filter(schedule => schedule.id !== id));
    };

    // Add new payment schedule row
    const addPaymentSchedule = () => {
        const newId = Math.max(...paymentSchedules.map(item => item.id)) + 1;
        const newSchedule = Math.max(...paymentSchedules.map(item => item.schedule)) + 1;
        const newPaymentSchedule = {
            id: newId,
            schedule: newSchedule,
            numPayments: "",
            paymentAmount: "",
            frequency: "Weekly",
            firstPaymentDate: "",
            finalPayment: ""
        };
        setPaymentSchedules([...paymentSchedules, newPaymentSchedule]);
    };

    // Update down payment schedule
    const updateDownPaymentSchedule = (id, field, value) => {
        setDownPaymentSchedules(prev =>
            prev.map(item =>
                item.id === id ? { ...item, [field]: value } : item
            )
        );
    };

    // Update payment schedule
    const updatePaymentSchedule = (id, field, value) => {
        setPaymentSchedules(prev =>
            prev.map(item =>
                item.id === id ? { ...item, [field]: value } : item
            )
        );
    };

    const handleCalculate = () => {
        // Reset error state
        setHasError(false);
        setErrorMessage("");

        // Validate required fields
        if (!contract || !compoundingMethod || !accrualMethod || !salesTaxDeferred || !daysInYear || !amortization) {
            setHasError(true);
            setErrorMessage(t('addAccount.pleaseEnsureValidPaymentSchedule'));
            return;
        }

        // TODO: Implement actual calculation logic here
        // For now, we'll simulate some calculated values
        setLoanRate("12.5000%");
        setApr("13.2500%");
        setFinanceCharge("$2,450.00");
        setAmountFinanced("$15,000.00");
        setTotalOfPayments("$17,450.00");
        setTotalDownPayment("$12.00");
    };

    return (
        <div className="card">
            <div className="card-body">
                {/* Contract Settings Section */}
                <div className="mb-4">
                    <h6 className='info-text mb-3'>{t('addAccount.contractSettings')}</h6>

                    <Row>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.contract')}</label>
                                <select
                                    className="form-control"
                                    style={inputStyle14}
                                    value={contract}
                                    onChange={(e) => setContract(e.target.value)}
                                >
                                    <option value="">{t('addAccount.selectContract')}</option>
                                    {CONTRACT_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.compoundingMethod')}</label>
                                <select
                                    className="form-control"
                                    style={inputStyle14}
                                    value={compoundingMethod}
                                    onChange={(e) => setCompoundingMethod(e.target.value)}
                                >
                                    <option value="">{t('addAccount.selectCompoundingMethod')}</option>
                                    {COMPOUNDING_METHOD_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.accrualMethod')}</label>
                                <select
                                    className="form-control"
                                    style={inputStyle14}
                                    value={accrualMethod}
                                    onChange={(e) => setAccrualMethod(e.target.value)}
                                >
                                    <option value="">{t('addAccount.selectAccrualMethod')}</option>
                                    {ACCRUAL_METHOD_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.salesTaxDeferred')}</label>
                                <select
                                    className="form-control"
                                    style={inputStyle14}
                                    value={salesTaxDeferred}
                                    onChange={(e) => setSalesTaxDeferred(e.target.value)}
                                >
                                    <option value="">{t('addAccount.selectOption')}</option>
                                    {SALES_TAX_DEFERRED_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.daysInYear')}</label>
                                <select
                                    className="form-control"
                                    style={inputStyle14}
                                    value={daysInYear}
                                    onChange={(e) => setDaysInYear(e.target.value)}
                                >
                                    <option value="">{t('addAccount.selectDaysInYear')}</option>
                                    {DAYS_IN_YEAR_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.amortization')}</label>
                                <select
                                    className="form-control"
                                    style={inputStyle14}
                                    value={amortization}
                                    onChange={(e) => setAmortization(e.target.value)}
                                >
                                    <option value="">{t('addAccount.selectAmortization')}</option>
                                    {AMORTIZATION_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                    </Row>

                    {/* Calculate Button */}
                    <div className="text-center mt-4">
                        <Button
                            color="primary"
                            size="lg"
                            onClick={handleCalculate}
                            style={{ minWidth: '200px' }}
                        >
                            {t('addAccount.calculate')}
                        </Button>
                    </div>

                    {/* Error Message */}
                    {hasError && (
                        <div className="alert alert-danger mt-3" role="alert">
                            {errorMessage}
                        </div>
                    )}
                </div>

                {/* REGZ DISCLOSURE RESULTS Section */}
                <div className="mb-4">
                    <h6 className='info-text mb-3'>{t('addAccount.regzDisclosureResults')}</h6>

                    <Row>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.loanRate')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={loanRate}
                                    readOnly
                                />
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.apr')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={apr}
                                    readOnly
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.financeCharge')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={financeCharge}
                                    readOnly
                                />
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.amountFinanced')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={amountFinanced}
                                    readOnly
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.totalOfPayments')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={totalOfPayments}
                                    readOnly
                                />
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.totalDownPayment')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={totalDownPayment}
                                    readOnly
                                />
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Balance Setup Method Section */}
                <div className="mb-4">
                    <h6 className='info-text mb-3'>{t('addAccount.balanceSetupMethod')}</h6>

                    <Row>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.balanceSetupMethod')}</label>
                                <select
                                    className="form-control"
                                    style={inputStyle14}
                                    value={balanceSetupMethod}
                                    onChange={(e) => setBalanceSetupMethod(e.target.value)}
                                >
                                    {BALANCE_SETUP_METHOD_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for='contractDate'>{t('addAccount.contractDate')}</Label>
                                <ReactDatetime
                                    inputProps={{
                                        className: "form-control",
                                        placeholder: t('addAccount.selectDate'),
                                        style: dateInputStyle
                                    }}
                                    value={contractDate ? formatDateForPicker(contractDate) : contractDate}
                                    onChange={(date) => setContractDate(date)}
                                    dateFormat="MM-DD-YYYY"
                                    timeFormat={false}
                                    closeOnSelect={true}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.originalFinanceCharge')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={originalFinanceCharge}
                                    onChange={(e) => setOriginalFinanceCharge(e.target.value)}
                                />
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.currentDiscountAmount')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={currentDiscountAmount}
                                    onChange={(e) => setCurrentDiscountAmount(e.target.value)}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.originalAmtFinanced')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={originalAmtFinanced}
                                    onChange={(e) => setOriginalAmtFinanced(e.target.value)}
                                />
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="form-group">
                                <label>{t('addAccount.originalSalesTax')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle14}
                                    value={originalSalesTax}
                                    onChange={(e) => setOriginalSalesTax(e.target.value)}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <div className="form-group">
                                <label>{t('addAccount.discountMethod')}</label>
                                <select
                                    className="form-control"
                                    style={inputStyle14}
                                    value={discountMethod}
                                    onChange={(e) => setDiscountMethod(e.target.value)}
                                >
                                    {DISCOUNT_METHOD_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Down Payment Schedule and Remaining Down Payment Section */}
                <Row className="mb-4">
                    <Col md="6">
                        <div className="mb-3">
                            <h6 className='info-text mb-3'>{t('addAccount.downPaymentSchedule')}</h6>
                            <Table className="mb-0">
                                <thead>
                                    <tr>
                                        <th>{t('addAccount.schedule')}</th>
                                        <th>{t('addAccount.origAmount')}</th>
                                        <th>{t('addAccount.dateDue')}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {downPaymentSchedules.map((schedule) => (
                                        <tr key={schedule.id}>
                                            <td>{schedule.type}</td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    style={inputStyle14}
                                                    value={schedule.origAmount}
                                                    onChange={(e) => updateDownPaymentSchedule(schedule.id, 'origAmount', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <ReactDatetime
                                                    inputProps={{
                                                        className: "form-control form-control-sm",
                                                        placeholder: t('addAccount.selectDate'),
                                                        style: dateInputStyle
                                                    }}
                                                    value={schedule.dateDue ? formatDateForPicker(schedule.dateDue) : schedule.dateDue}
                                                    onChange={(date) => updateDownPaymentSchedule(schedule.id, 'dateDue', date)}
                                                    dateFormat="MM-DD-YYYY"
                                                    timeFormat={false}
                                                    closeOnSelect={true}
                                                />
                                            </td>
                                            <td>
                                                {schedule.id > 1 && (
                                                <Button
                                                    size="sm"
                                                    disabled={downPaymentSchedules.length <= 1}
                                                    color="success"
                                                    onClick={() => removeDownPaymentSchedule(schedule.id)}
                                                    style={{ padding: '2px 8px' }}
                                                >
                                                        -
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                                <Button
                                    size="sm"
                                    disabled={downPaymentSchedules.length >= 6}
                                    onClick={addDownPaymentSchedule}
                                    style={{ padding: '2px 8px' }}
                                >
                                    +
                                </Button>
                            </div>
                        </div>
                    </Col>
                    <Col md="6">
                        <div className="mb-3">
                            <h6 className='info-text mb-3'>{t('addAccount.remainingDownPayment')}</h6>
                            <div
                                style={{
                                    height: '200px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#6c757d'
                                }}
                            >
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Payment Schedule Section */}
                <div className="mb-4">
                    <h6 className='info-text mb-3'>{t('addAccount.paymentSchedule')}</h6>
                    <Table >
                        <thead>
                            <tr>
                                <th>{t('addAccount.schedule')}</th>
                                <th>{t('addAccount.numPayments')}</th>
                                <th>{t('addAccount.paymentAmount')}</th>
                                <th>{t('addAccount.frequency')}</th>
                                <th>{t('addAccount.firstPaymentDate')}</th>
                                <th>{t('addAccount.finalPayment')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {paymentSchedules.map((schedule) => (
                                <tr key={schedule.id}>
                                    <td>{schedule.schedule}</td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            style={inputStyle14}
                                            value={schedule.numPayments}
                                            onChange={(e) => updatePaymentSchedule(schedule.id, 'numPayments', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            style={inputStyle14}
                                            value={schedule.paymentAmount}
                                            onChange={(e) => updatePaymentSchedule(schedule.id, 'paymentAmount', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="form-control form-control-sm"
                                            style={inputStyle14}
                                            value={schedule.frequency}
                                            onChange={(e) => updatePaymentSchedule(schedule.id, 'frequency', e.target.value)}
                                        >
                                            {FREQUENCY_OPTIONS.map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <ReactDatetime
                                            inputProps={{
                                                className: "form-control form-control-sm",
                                                placeholder: t('addAccount.selectDate'),
                                                style: dateInputStyle
                                            }}
                                            value={schedule.firstPaymentDate ? formatDateForPicker(schedule.firstPaymentDate) : schedule.firstPaymentDate}
                                            onChange={(date) => updatePaymentSchedule(schedule.id, 'firstPaymentDate', date)}
                                            dateFormat="MM-DD-YYYY"
                                            timeFormat={false}
                                            closeOnSelect={true}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            style={inputStyle14}
                                            value={schedule.finalPayment}
                                            onChange={(e) => updatePaymentSchedule(schedule.id, 'finalPayment', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        {schedule.schedule === 1 && (
                                            <Button
                                                size="sm"
                                                color="success"
                                                onClick={addPaymentSchedule}
                                                style={{ padding: '2px 8px' }}
                                            >
                                                +
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default LoanBalance;
